import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core'

import { Store } from '@ngrx/store'
import { BreadcrumbModule } from 'primeng/breadcrumb'

import {
  HeaderAction,
  HeaderObject,
} from '@flowx/designer/modules/analytics/analytics-header.entity'
import {
  selectAnalyticsArea,
  selectAnalyticsResourceType,
} from '@flowx/designer/modules/analytics/analytics-header.selectors'
import { AnalyticsService } from '@flowx/designer/modules/analytics/analytics.service'
import { SeverityMarkerComponent } from '@flowx/designer/modules/misconfigurations/components/severity-marker/severity-marker.component'
import {
  BreadcrumbItemClickEvent,
  BreadcrumbsComponent,
  FlxBreadcrumbItem,
} from '@flowx/designer/shared/components/breadcrumbs/breadcrumbs.component'

/** The subheader: breadcrumbs, which are also the way back, and the page's own actions. */
@Component({
  selector: 'flx-resource-header',
  imports: [BreadcrumbModule, BreadcrumbsComponent, NgClass, SeverityMarkerComponent],
  templateUrl: './resource-header.component.html',
  styleUrls: ['./resource-header.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  host: { '[class.dark]': 'darkMode()', '[class.light]': '!darkMode()' },
})
export class FlxResourceHeaderComponent {
  #analyticsService = inject(AnalyticsService)
  #store = inject(Store)
  #analyticsArea = this.#store.selectSignal(selectAnalyticsArea)
  #analyticsResourceType = this.#store.selectSignal(selectAnalyticsResourceType)

  breadcrumbs = input<FlxBreadcrumbItem[]>([])
  darkMode = input<boolean>(true)

  /**
   * Misconfiguration errors for the open resource. `null` — the default — renders no badge, which
   * is also what a cold start and a hidden-markers preference resolve to.
   */
  errorCount = input<number | null>(null)

  errorBadgeCount = computed((): number => this.errorCount() ?? 0)

  trackBreadcrumbClick({ item }: BreadcrumbItemClickEvent): void {
    // The open page's own segment leads nowhere.
    if (!item.routerLink && !item.command && !item.url) {
      return
    }

    const resourceType = this.#analyticsResourceType()

    this.#analyticsService.trackHeader({
      event: { object: HeaderObject.BREADCRUMB, action: HeaderAction.CLICKED },
      properties: {
        segment: 'parent_list',
        area: this.#analyticsArea(),
        // Left off on pages whose route names no type, such as a theme or an org detail page.
        ...(resourceType && { resourceType }),
      },
    })
  }
}
